/**
 * Luxe DesignScene 2D Front-Elevation Designer — main container.
 * Sub-components live in ./SchematicPanels.jsx so this file stays
 * traversable by the visual-edits babel plugin.
 *
 * Coordinate system:
 *   X = horizontal along wall (mm), 0 at left
 *   Y = height above floor (mm), 0 at floor (FFL)
 *   Z = projection from wall face (mm), positive TOWARD room
 */
import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import {
  Lock, Undo2, Redo2, Sparkles, ZoomIn, ZoomOut, Maximize,
  ChevronRight, ChevronDown, Search, X,
} from "lucide-react";
import api from "@/lib/api";
import {
  CATEGORIES, componentsByCategory, PRESETS, buildPreset, getComponent,
} from "@/lib/componentRegistry";
import { symbolFor } from "@/lib/drawingSymbolRegistry";
import {
  SelectedHeader, PropertiesBody, SchematicItemsBody,
  SelectLayerPicker, DeleteConfirm, SaveIndicator, EstimateIndicator,
} from "@/components/SchematicPanels";
import { CabinetBayPreview } from "@/components/CabinetBayPreview";
import {
  getClearanceEnvelope,
  resolveBlockingClamp,
  computeConflicts,
  collectBlockingEnvelopes,
  collectAllClearanceEnvelopes,
  snapToClearanceBoundaries,
} from "@/lib/clearanceResolver";

const SNAP_MM = 25;
// Canonical Clearance-boundary magnetic snap tolerance (mm).
// Larger than convenience grid snap so a pointer approaching a
// dashed keep-out line reliably acquires the exact canonical
// boundary. Blocking clamp still has final authority.
const CLEARANCE_SNAP_MM = 40;
const HISTORY_LIMIT = 50;
const AUTOSAVE_DEBOUNCE_MS = 800;
const LOCAL_KEY = (pid) => `luxe.scene.recovery.${pid}`;

export default function SchematicBuilder({
  wallW = 3000, wallH = 2400,
  items = [], onChange,
  products = [],
  onSyncItem,
  projectId,
  wallDepth = 400,
  readOnly = false,
  clientView = false,
}) {
  const canvasRef = useRef(null);
  const [selectedId, setSelectedId] = useState(null);
  const [drag, setDrag] = useState(null);
  const [zoom, setZoom] = useState(1);
  const [pan, setPan] = useState({ x: 0, y: 0 });
  const [saveState, setSaveState] = useState("idle");
  const [estimateStatus, setEstimateStatus] = useState({ current: null, dirty: false });
  const [showPresets, setShowPresets] = useState(false);
  const [containerW, setContainerW] = useState(1000);
  const [rightTab, setRightTab] = useState("properties");
  const [itemsView, setItemsView] = useState("draw");
  const [itemsSearch, setItemsSearch] = useState("");
  const [multiHitPicker, setMultiHitPicker] = useState(null);
  const [pendingDelete, setPendingDelete] = useState(null);
  // ── Clearance drag/snap enforcement state ─────────────────────
  // Populated during a drag when the moving component conflicts with
  // another item's clearance envelope. Drives visual feedback + the
  // Blocking clamp. Cleared on pointer-up. Never persisted.
  const [dragConflicts, setDragConflicts] = useState({ blocking: [], warn: [], advisory: [] });
  // ── Add-Element palette UI state ────────────────────────────────
  // Collapsible category headers. Default: only the first category
  // (`Structure`) open; the rest collapsed to eliminate the long-list
  // scrolling. State persists across the Studio session via
  // sessionStorage but is UI-only — never persisted to scene_versions
  // or scene_hash.
  const [paletteSearch, setPaletteSearch] = useState("");
  const [expandedCats, setExpandedCats] = useState(() => {
    try {
      const raw = sessionStorage.getItem("luxe.palette.expandedCats");
      if (raw) return JSON.parse(raw);
    } catch { /* noop */ }
    const init = {};
    CATEGORIES.forEach((c, i) => { init[c] = (i === 0); });
    return init;
  });
  useEffect(() => {
    try { sessionStorage.setItem("luxe.palette.expandedCats", JSON.stringify(expandedCats)); }
    catch { /* noop */ }
  }, [expandedCats]);
  const toggleCategory = (cat) =>
    setExpandedCats((s) => ({ ...s, [cat]: !s[cat] }));
  // Track mousedown target so canvas onClick can distinguish a genuine
  // empty-canvas click from one where mousedown originated on a scene
  // item / overlay / handle but the browser dispatched the click event
  // on the shared ancestor (canvas) because mousedown and mouseup hit
  // different children (SceneItem → SelectionInteractionOverlay after
  // a state update in between).
  const mousedownInsideItemRef = useRef(false);
  // True if the most recent pointer sequence exceeded the click/drag
  // threshold — consumed (and reset) by the next click event to skip
  // the overlap-cycling picker after a real drag. See handleItemClick.
  const draggedRef = useRef(false);

  const altKeyRef = useRef(false);
  const historyRef = useRef({ past: [], future: [], current: null });
  const autosaveTimerRef = useRef(null);
  const itemsListRef = useRef(null);

  useEffect(() => {
    const el = canvasRef.current;
    if (!el) return;
    const upd = () => setContainerW(el.getBoundingClientRect().width || 1000);
    upd();
    const ro = new ResizeObserver(upd);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  const baseScale = useMemo(() => {
    const padPx = 40;
    const availW = Math.max(400, containerW - padPx);
    const targetH = Math.max(300, Math.min(600, wallH * (availW / wallW)));
    return Math.min(availW / wallW, targetH / wallH);
  }, [containerW, wallW, wallH]);
  const scale = baseScale * zoom;

  const svgToMM = useCallback((cx, cy) => {
    const rect = canvasRef.current.getBoundingClientRect();
    const relX = cx - rect.left - 20 - pan.x;
    const relY = cy - rect.top - 20 - pan.y;
    return { x: relX / scale, y: (wallH * scale - relY) / scale };
  }, [scale, pan, wallH]);

  const clamp = (v, mn, mx) => Math.max(mn, Math.min(mx, v));
  const snap = (v) => altKeyRef.current ? v : Math.round(v / SNAP_MM) * SNAP_MM;

  useEffect(() => {
    if (historyRef.current.current === null) {
      historyRef.current.current = JSON.stringify(items);
    }
  }, [items]);

  const commitHistory = useCallback((newItems) => {
    const snapshot = JSON.stringify(newItems);
    const prev = historyRef.current.current;
    if (snapshot === prev) return;
    historyRef.current.past = [
      ...historyRef.current.past.slice(-HISTORY_LIMIT + 1),
      prev ?? snapshot,
    ];
    historyRef.current.future = [];
    historyRef.current.current = snapshot;
  }, []);

  const applyItems = useCallback((newItems, opts = {}) => {
    if (!opts.skipHistory) commitHistory(newItems);
    onChange(newItems);
  }, [onChange, commitHistory]);

  const undo = useCallback(() => {
    const h = historyRef.current;
    if (!h.past.length) return;
    const prev = h.past.pop();
    h.future.push(h.current);
    h.current = prev;
    try { onChange(JSON.parse(prev)); } catch { /* ignore */ }
  }, [onChange]);

  const redo = useCallback(() => {
    const h = historyRef.current;
    if (!h.future.length) return;
    const next = h.future.pop();
    h.past.push(h.current);
    h.current = next;
    try { onChange(JSON.parse(next)); } catch { /* ignore */ }
  }, [onChange]);

  useEffect(() => {
    if (!projectId) return;
    if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current);
    autosaveTimerRef.current = setTimeout(async () => {
      setSaveState("saving");
      try {
        const { data } = await api.patch(`/projects/${projectId}/scene`, {
          wall_width_mm: wallW, wall_height_mm: wallH, wall_depth_mm: wallDepth,
          items: items.map(_serialiseItem),
        });
        setEstimateStatus({ current: data.estimate_current, dirty: !data.estimate_current });
        setSaveState("saved");
        try { localStorage.removeItem(LOCAL_KEY(projectId)); } catch { /* ignore */ }
      } catch {
        try {
          localStorage.setItem(LOCAL_KEY(projectId), JSON.stringify({
            items, wallW, wallH, wallDepth, savedAt: Date.now(),
          }));
          setSaveState("offline");
        } catch { setSaveState("error"); }
      }
    }, AUTOSAVE_DEBOUNCE_MS);
    return () => { if (autosaveTimerRef.current) clearTimeout(autosaveTimerRef.current); };
    // eslint-disable-next-line
  }, [items, wallW, wallH, wallDepth, projectId]);

  useEffect(() => {
    if (!projectId) return;
    try {
      const raw = localStorage.getItem(LOCAL_KEY(projectId));
      if (!raw) return;
      const rec = JSON.parse(raw);
      if (Array.isArray(rec.items) && rec.items.length > (items || []).length) {
        const ok = window.confirm(
          "Unsaved local design changes were found for this project. Restore them?\n\n" +
          `Local: ${rec.items.length} items · Server: ${(items || []).length} items`,
        );
        if (ok) applyItems(rec.items);
        localStorage.removeItem(LOCAL_KEY(projectId));
      }
    } catch { /* ignore */ }
    // eslint-disable-next-line
  }, [projectId]);

  useEffect(() => {
    const onKey = (e) => {
      if (e.key === "Alt") altKeyRef.current = true;
      const tag = e.target.tagName;
      if (tag === "INPUT" || tag === "TEXTAREA" || tag === "SELECT") return;
      const mod = e.ctrlKey || e.metaKey;
      if (mod && e.key.toLowerCase() === "z" && !e.shiftKey) { e.preventDefault(); undo(); return; }
      if (mod && (e.key.toLowerCase() === "y" || (e.shiftKey && e.key.toLowerCase() === "z"))) {
        e.preventDefault(); redo(); return;
      }
      if (!selectedId) return;
      const it = items.find((x) => x.id === selectedId);
      if (!it) return;
      if (e.key === "Delete" || e.key === "Backspace") {
        e.preventDefault(); requestDelete(it);
      } else if (mod && e.key.toLowerCase() === "d") {
        e.preventDefault(); duplicateItem(it);
      }
    };
    const onKeyUp = (e) => { if (e.key === "Alt") altKeyRef.current = false; };
    window.addEventListener("keydown", onKey);
    window.addEventListener("keyup", onKeyUp);
    return () => {
      window.removeEventListener("keydown", onKey);
      window.removeEventListener("keyup", onKeyUp);
    };
    // eslint-disable-next-line
  }, [selectedId, items, undo, redo]);

  const nextDrawOrder = () => {
    let m = 0;
    for (const it of items) m = Math.max(m, it.draw_order || 0);
    return m + 1;
  };

  const addItem = (type) => {
    const def = getComponent(type);
    if (!def) return;
    const id = `it_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`;
    const w = Math.min(def.w, wallW * 0.9);
    const h = Math.min(def.h, wallH * 0.9);
    const newItem = {
      id, type,
      x: Math.max(0, (wallW - w) / 2),
      y: Math.max(0, wallH * 0.35),
      z: 0,
      width: w, height: h, depth: def.d,
      rotation: 0,
      material_id: null,
      semantic_type: def.semantic,
      quantity_mode: def.default_qm,
      label: def.label,
      locked: false,
      draw_order: nextDrawOrder(),
    };
    applyItems([...items, newItem]);
    setSelectedId(id);
    setRightTab("properties");
  };

  const insertPreset = (presetKey) => {
    const generated = buildPreset(presetKey, wallW, wallH);
    if (!generated.length) return;
    const base = nextDrawOrder();
    const withOrder = generated.map((g, i) => ({ ...g, draw_order: base + i }));
    applyItems([...items, ...withOrder]);
    setShowPresets(false);
  };

  const duplicateItem = (item) => {
    if (!item) return;
    const dup = {
      ...item,
      id: `it_${Date.now().toString(36)}_${Math.random().toString(36).slice(2, 6)}`,
      x: Math.min(wallW - item.width, item.x + 100),
      y: Math.max(0, item.y - 100),
      linked_line_item_id: undefined,
      calculated_quantity: undefined,
      draw_order: nextDrawOrder(),
    };
    if (item.material_slots) dup.material_slots = JSON.parse(JSON.stringify(item.material_slots));
    if (item.clearances)     dup.clearances     = JSON.parse(JSON.stringify(item.clearances));
    applyItems([...items, dup]);
    setSelectedId(dup.id);
    setRightTab("properties");
  };

  const requestDelete = (item) => {
    if (!item) return;
    if (item.locked) { toast.error("Unlock to delete"); return; }
    const children = items.filter((i) => i.host_id === item.id);
    setPendingDelete({ item, children });
  };

  const performDelete = (mode) => {
    if (!pendingDelete) return;
    const { item, children } = pendingDelete;
    let next = items;
    if (mode === "cascade" && children.length) {
      const gone = new Set([item.id, ...children.map((c) => c.id)]);
      next = items.filter((i) => !gone.has(i.id));
    } else {
      next = items
        .filter((i) => i.id !== item.id)
        .map((i) => (i.host_id === item.id ? { ...i, host_id: null } : i));
    }
    const remaining = new Set(next.map((i) => i.id));
    next = next.map((i) => {
      if (!i.clearances) return i;
      const cleaned = i.clearances.filter((c) => !c.ref_id || remaining.has(c.ref_id));
      return cleaned.length === (i.clearances || []).length ? i : { ...i, clearances: cleaned };
    });
    applyItems(next);
    if (selectedId === item.id) setSelectedId(null);
    setPendingDelete(null);
  };

  const centerH = () => selected && updateSelected({ x: Math.round((wallW - selected.width) / 2) });
  const centerV = () => selected && updateSelected({ y: Math.round((wallH - selected.height) / 2) });
  const centerBoth = () => { centerH(); centerV(); };
  const alignLeft   = () => selected && updateSelected({ x: 0 });
  const alignRight  = () => selected && updateSelected({ x: Math.max(0, wallW - selected.width) });
  const alignBottom = () => selected && updateSelected({ y: 0 });
  const alignTop    = () => selected && updateSelected({ y: Math.max(0, wallH - selected.height) });

  const bringForward = () => {
    if (!selected) return;
    const order = [...items].sort((a, b) => (a.draw_order || 0) - (b.draw_order || 0));
    const idx = order.findIndex((i) => i.id === selected.id);
    if (idx < 0 || idx === order.length - 1) return;
    const above = order[idx + 1];
    const aDo = above.draw_order || 0;
    const sDo = selected.draw_order || 0;
    applyItems(items.map((i) => {
      if (i.id === selected.id) return { ...i, draw_order: aDo };
      if (i.id === above.id)    return { ...i, draw_order: sDo };
      return i;
    }));
  };
  const sendBackward = () => {
    if (!selected) return;
    const order = [...items].sort((a, b) => (a.draw_order || 0) - (b.draw_order || 0));
    const idx = order.findIndex((i) => i.id === selected.id);
    if (idx <= 0) return;
    const below = order[idx - 1];
    const bDo = below.draw_order || 0;
    const sDo = selected.draw_order || 0;
    applyItems(items.map((i) => {
      if (i.id === selected.id) return { ...i, draw_order: bDo };
      if (i.id === below.id)    return { ...i, draw_order: sDo };
      return i;
    }));
  };
  const bringToFront = () => {
    if (!selected) return;
    let m = 0; for (const it of items) m = Math.max(m, it.draw_order || 0);
    updateSelected({ draw_order: m + 1 });
  };
  const sendToBack = () => {
    if (!selected) return;
    let m = 0; for (const it of items) m = Math.min(m, it.draw_order || 0);
    updateSelected({ draw_order: m - 1 });
  };

  const startMove = (e, item) => {
    if (readOnly) return;
    if (item.locked) return;
    e.stopPropagation();
    setSelectedId(item.id);
    const p = svgToMM(e.clientX ?? e.touches?.[0].clientX, e.clientY ?? e.touches?.[0].clientY);
    setDrag({ id: item.id, kind: "move", startMM: p, orig: { x: item.x, y: item.y } });
  };
  const startResize = (e, item, handle = "br") => {
    if (readOnly) return;
    if (item.locked) return;
    e.stopPropagation();
    setSelectedId(item.id);
    const p = svgToMM(e.clientX ?? e.touches?.[0].clientX, e.clientY ?? e.touches?.[0].clientY);
    // Freeze the FULL geometry + the two anchored physical edges. Every
    // corner keeps its diagonally-opposite corner physically fixed. All
    // maths happens in canonical mm-space (Y up from floor).
    const right = item.x + item.width;
    const top   = item.y + item.height;
    setDrag({
      id: item.id, kind: "resize", handle, startMM: p,
      orig: { x: item.x, y: item.y, width: item.width, height: item.height, right, top },
    });
  };

  useEffect(() => {
    if (!drag) return;
    // Snapshot the pre-drag legal position for anti-tunnelling. This is
    // the last position we know was legal — Blocking clamp uses it to
    // resolve approach direction so fast/diagonal pointer jumps cannot
    // penetrate a protected envelope.
    const origPos = { x: drag.orig.x, y: drag.orig.y };
    const onMove = (ev) => {
      const evt = ev.touches ? ev.touches[0] : ev;
      const cur = svgToMM(evt.clientX, evt.clientY);
      const dx = cur.x - drag.startMM.x;
      const dy = cur.y - drag.startMM.y;
      // Mark real drag so the trailing click event skips overlap-cycling.
      if (Math.abs(dx) > 2 || Math.abs(dy) > 2) draggedRef.current = true;
      let liveConflicts = { blocking: [], warn: [], advisory: [] };
      const newItems = items.map((it) => {
        if (it.id !== drag.id) return it;
        if (drag.kind === "move") {
          // ── 1. Pointer candidate + convenience snap ────────────
          let nx = snap(drag.orig.x + dx);
          let ny = snap(drag.orig.y + dy);
          // ── 2. Wall bounds ─────────────────────────────────────
          nx = clamp(nx, 0, wallW - it.width);
          ny = clamp(ny, 0, wallH - it.height);
          // ── 3. Neighbour edge snapping (convenience) ───────────
          if (!altKeyRef.current) {
            const cx = nx + it.width / 2;
            if (Math.abs(cx - wallW / 2) < 30) nx = wallW / 2 - it.width / 2;
            if (Math.abs(nx) < 30) nx = 0;
            if (Math.abs((nx + it.width) - wallW) < 30) nx = wallW - it.width;
            if (Math.abs(ny) < 30) ny = 0;
            for (const o of items) {
              if (o.id === it.id) continue;
              if (Math.abs(nx - o.x) < 30) nx = o.x;
              if (Math.abs((nx + it.width) - (o.x + o.width)) < 30) nx = o.x + o.width - it.width;
              if (Math.abs(ny - o.y) < 30) ny = o.y;
              if (Math.abs((ny + it.height) - (o.y + o.height)) < 30) ny = o.y + o.height - it.height;
            }
          }
          // ── 3.5. Clearance boundary magnetic snap (canonical) ──
          //   Snaps physical edges (not centre) to exact canonical
          //   Keep-Out boundaries within CLEARANCE_SNAP_MM. Overrides
          //   convenience grid snap when in range. Snap-Off (Alt) also
          //   disables this magnetic behaviour, but Blocking clamp
          //   below still enforces a hard boundary.
          let snapEdges = {};
          if (!altKeyRef.current) {
            const envs = collectAllClearanceEnvelopes(it.id, items);
            const s = snapToClearanceBoundaries(nx, ny, it.width, it.height, envs, CLEARANCE_SNAP_MM);
            nx = clamp(s.x, 0, wallW - it.width);
            ny = clamp(s.y, 0, wallH - it.height);
            snapEdges = s.snappedEdges;
          }
          // ── 4. Blocking Clearance clamp (overrides snap) ────────
          //   Clearance is a constraint, snapping is a convenience.
          //   Convenience snap runs first; Blocking then overrides so
          //   grid/edge snap can never force a component inside a
          //   Blocking Clearance. Snap-Off (alt/⌥) disables convenience
          //   snapping but does NOT disable Blocking — Blocking always
          //   applies. Legacy pre-existing overlaps are preserved by
          //   `resolveBlockingClamp` (see resolver docstring).
          const blockers = collectBlockingEnvelopes(it.id, items);
          // Compute conflicts against the PRE-clamp candidate so
          // Blocking overlays visibly light up whenever the resolver
          // clamps. Advisory/Warn levels are unaffected by the clamp
          // (their positions were never modified), so pre-clamp
          // conflicts are the authoritative feedback signal.
          const preClampConflicts = computeConflicts(
            it.id, nx, ny, it.width, it.height, items,
          );
          const resolved = resolveBlockingClamp(
            origPos, { x: nx, y: ny }, it.width, it.height, blockers,
          );
          nx = resolved.x;
          ny = resolved.y;
          // Re-clamp to wall bounds in case Blocking clamp pushed us
          // outside the wall (edge case where wall corner is inside a
          // blocker with legacy overlap).
          nx = clamp(nx, 0, wallW - it.width);
          ny = clamp(ny, 0, wallH - it.height);
          // ── 5. Conflict feedback for the current pointer frame ──
          //   Blocking bucket = pre-clamp candidate hits (visible even
          //   though the component is now held at boundary).
          //   Warn / Advisory buckets = post-clamp position hits.
          const postConflicts = computeConflicts(
            it.id, nx, ny, it.width, it.height, items,
          );
          liveConflicts = {
            blocking: preClampConflicts.blocking,
            warn:     postConflicts.warn,
            advisory: postConflicts.advisory,
          };
          return { ...it, x: nx, y: ny };
        }
        // ── Four-corner resize ────────────────────────────────────
        // Each corner keeps its diagonally opposite corner physically
        // anchored in canonical mm-space (Y up from floor, so y = FFL,
        // y + height = top). See docs/handle_math in README block above.
        const h = drag.handle || "br";
        const { x: ox, y: oy, width: ow, height: oh, right: oRight, top: oTop } = drag.orig;
        const rawX  = snap(cur.x);
        const rawY  = snap(cur.y);
        let nx = ox, ny = oy, nw = ow, nh = oh;
        if (h === "br") {
          // anchor Top-Left (x, top). x stays, top stays.
          nx = ox;
          nw = clamp(rawX - ox, SNAP_MM, wallW - ox);
          ny = clamp(rawY, 0, oTop - SNAP_MM);
          nh = oTop - ny;
        } else if (h === "bl") {
          // anchor Top-Right (right, top). right stays, top stays.
          nx = clamp(rawX, 0, oRight - SNAP_MM);
          nw = oRight - nx;
          ny = clamp(rawY, 0, oTop - SNAP_MM);
          nh = oTop - ny;
        } else if (h === "tr") {
          // anchor Bottom-Left (x, y). x stays, y stays (FFL fixed).
          nx = ox;
          ny = oy;
          nw = clamp(rawX - ox, SNAP_MM, wallW - ox);
          nh = clamp(rawY - oy, SNAP_MM, wallH - oy);
        } else if (h === "tl") {
          // anchor Bottom-Right (right, y). right stays, y stays.
          nx = clamp(rawX, 0, oRight - SNAP_MM);
          nw = oRight - nx;
          ny = oy;
          nh = clamp(rawY - oy, SNAP_MM, wallH - oy);
        }
        // ── Clearance boundary snap during resize (Phase 2B add-on) ─
        //   The physical edge being dragged snaps to the exact canonical
        //   Clearance boundary when within CLEARANCE_SNAP_MM. Opposite
        //   edge remains anchored — width/height re-derive from the
        //   snapped edge. Blocking clamp below still enforces hard
        //   constraint. Alt (snap-off) disables magnetic attraction.
        if (!altKeyRef.current) {
          const envs = collectAllClearanceEnvelopes(it.id, items);
          for (const env of envs) {
            // Left handle → snaps left edge to env.right
            if ((h === "tl" || h === "bl") && Math.abs(nx - env.right) <= CLEARANCE_SNAP_MM) {
              const oldRight = nx + nw;
              nx = env.right;
              nw = Math.max(SNAP_MM, oldRight - nx);
            }
            // Right handle → snaps right edge to env.left
            if ((h === "tr" || h === "br") && Math.abs((nx + nw) - env.left) <= CLEARANCE_SNAP_MM) {
              nw = Math.max(SNAP_MM, env.left - nx);
            }
            // Bottom handle → snaps bottom (ny) to env.top
            if ((h === "bl" || h === "br") && Math.abs(ny - env.top) <= CLEARANCE_SNAP_MM) {
              const oldTop = ny + nh;
              ny = env.top;
              nh = Math.max(SNAP_MM, oldTop - ny);
            }
            // Top handle → snaps top (ny + nh) to env.bottom
            if ((h === "tl" || h === "tr") && Math.abs((ny + nh) - env.bottom) <= CLEARANCE_SNAP_MM) {
              nh = Math.max(SNAP_MM, env.bottom - ny);
            }
          }
        }
        // ── Blocking clamp on resize — refuse penetration ──────────
        //   For every Blocking envelope from OTHER items, if the
        //   resized rectangle would overlap it, clamp the resizing
        //   edge to the boundary. Owner never blocks against itself.
        {
          const blockers = collectBlockingEnvelopes(it.id, items);
          for (const env of blockers) {
            // Overlap test: reject when new rect overlaps envelope.
            const overlaps = nx < env.right && nx + nw > env.left
                          && ny < env.top   && ny + nh > env.bottom;
            if (!overlaps) continue;
            if (h === "tl" || h === "bl") {
              // Left handle — force nx up to env.right, re-derive width.
              const oldRight = ox + ow;
              nx = Math.max(nx, env.right);
              nw = Math.max(SNAP_MM, oldRight - nx);
            }
            if (h === "tr" || h === "br") {
              nw = Math.max(SNAP_MM, env.left - nx);
            }
            if (h === "bl" || h === "br") {
              const oldTop = oy + oh;
              ny = Math.max(ny, env.top);
              nh = Math.max(SNAP_MM, oldTop - ny);
            }
            if (h === "tl" || h === "tr") {
              nh = Math.max(SNAP_MM, env.bottom - ny);
            }
          }
        }
        // Emit conflict feedback during resize so the ConflictOverlay
        // lights up Advisory / Warning / Blocking edges exactly like
        // during a move drag.
        const resizeConflicts = computeConflicts(it.id, nx, ny, nw, nh, items);
        liveConflicts = {
          blocking: resizeConflicts.blocking,
          warn:     resizeConflicts.warn,
          advisory: resizeConflicts.advisory,
        };
        return { ...it, x: nx, y: ny, width: nw, height: nh };
      });
      onChange(newItems);
      setDragConflicts(liveConflicts);
    };
    const onUp = () => {
      // ── Final-drop validation (defence-in-depth) ─────────────────
      // Ensures no invalid Blocking position ever becomes canonical
      // state even if a pointer event was missed during the drag. Runs
      // Blocking clamp one more time against the current committed
      // position. Advisory/Warn positions are permitted.
      if (drag.kind === "move") {
        const final = items.find((i) => i.id === drag.id);
        if (final) {
          const blockers = collectBlockingEnvelopes(final.id, items);
          const resolved = resolveBlockingClamp(
            origPos, { x: final.x, y: final.y },
            final.width, final.height, blockers,
          );
          if (resolved.x !== final.x || resolved.y !== final.y) {
            const safe = items.map((it) => it.id === final.id
              ? { ...it, x: resolved.x, y: resolved.y } : it);
            onChange(safe);
          }
        }
      }
      commitHistory(items);
      setDrag(null);
      setDragConflicts({ blocking: [], warn: [], advisory: [] });
    };
    window.addEventListener("mousemove", onMove);
    window.addEventListener("touchmove", onMove, { passive: false });
    window.addEventListener("mouseup", onUp);
    window.addEventListener("touchend", onUp);
    return () => {
      window.removeEventListener("mousemove", onMove);
      window.removeEventListener("touchmove", onMove);
      window.removeEventListener("mouseup", onUp);
      window.removeEventListener("touchend", onUp);
    };
    // eslint-disable-next-line
  }, [drag, items, wallW, wallH]);

  const selected = items.find((i) => i.id === selectedId);

  const updateSelected = (patch) => {
    applyItems(items.map((it) => (it.id === selectedId ? { ...it, ...patch } : it)));
  };

  const assignMaterial = (product) => {
    if (!selected) return;
    updateSelected({ material_id: product.id, product_id: product.id });
  };

  // Multi-hit test — returns items at pointer, sorted front→back.
  const hitTest = useCallback((clientX, clientY) => {
    const rect = canvasRef.current?.getBoundingClientRect();
    if (!rect) return [];
    const mm = {
      x: (clientX - rect.left - 20 - pan.x) / scale,
      y: (wallH * scale - (clientY - rect.top - 20 - pan.y)) / scale,
    };
    const hits = [];
    items.forEach((it, idx) => {
      const def = getComponent(it.type) || {};
      if (def.is_symbol) {
        const symPx = def.symbol_px || 22;
        const symMm = symPx / scale;
        const cx = it.x + it.width / 2;
        const cy = it.y + it.height / 2;
        if (mm.x >= cx - symMm / 2 && mm.x <= cx + symMm / 2 &&
            mm.y >= cy - symMm / 2 && mm.y <= cy + symMm / 2) {
          hits.push({ it, idx });
        }
        return;
      }
      if (mm.x >= it.x && mm.x <= it.x + it.width &&
          mm.y >= it.y && mm.y <= it.y + it.height) {
        hits.push({ it, idx });
      }
    });
    hits.sort((a, b) => {
      const ao = a.it.draw_order || 0;
      const bo = b.it.draw_order || 0;
      if (ao !== bo) return bo - ao;
      return b.idx - a.idx;
    });
    return hits.map((h) => h.it);
  }, [items, pan, scale, wallH]);

  const handleItemClick = (e, item) => {
    e.stopPropagation();
    // If the pointer just completed a real drag, do NOT cycle —
    // consume the flag and keep the current selection.
    if (draggedRef.current) {
      draggedRef.current = false;
      setSelectedId(item.id);
      setRightTab("properties");
      return;
    }
    // TRUE DIRECT CANVAS CYCLING. Build the full front→back hit stack
    // under the pointer. If the currently-selected item is in the
    // stack, advance to the next one (wrapping to front after the
    // back). Otherwise select the frontmost. Never auto-mount the
    // Select Layer picker on a normal click — the picker is a
    // secondary precision tool invoked explicitly (right-click /
    // context menu / dedicated Layers button).
    const hits = hitTest(e.clientX, e.clientY);
    setMultiHitPicker(null);
    if (hits.length === 0) return;
    const curIdx = hits.findIndex((h) => h.id === selectedId);
    const next = curIdx === -1 ? hits[0] : hits[(curIdx + 1) % hits.length];
    setSelectedId(next.id);
    setRightTab("properties");
  };

  // Explicit Select Layer picker invocation (right-click / context
  // menu). Never fired by a normal click. Available on canvas items
  // and on the SelectionInteractionOverlay so buried-item precision
  // selection remains reachable when a stack has 5+ items or the user
  // prefers a named list. Long-press on touch reuses the same logic.
  const openLayerPicker = (e) => {
    e.preventDefault();
    e.stopPropagation();
    const hits = hitTest(e.clientX, e.clientY);
    if (hits.length < 2) return;
    const rect = canvasRef.current.getBoundingClientRect();
    setMultiHitPicker({ x: e.clientX - rect.left, y: e.clientY - rect.top, hits });
  };

  const doSyncItem = async () => {
    if (!selected || !onSyncItem) return;
    const hasSlot = Object.values(selected.material_slots || {}).some((s) => s && (s.material_id || s.product_id));
    if (!selected.product_id && !selected.material_id && !hasSlot) {
      toast.error("Assign a material first");
      return;
    }
    try { await onSyncItem(selected.id); } catch { /* parent shows toast */ }
    if (projectId) {
      try {
        const { data } = await api.get(`/projects/${projectId}/scene/quantity-preview`);
        setEstimateStatus({ current: data.estimate_current, dirty: !data.estimate_current });
      } catch { /* keep prior */ }
    }
  };

  const grouped = useMemo(() => componentsByCategory(), []);
  const zoomIn = () => setZoom((z) => Math.min(4, z * 1.2));
  const zoomOut = () => setZoom((z) => Math.max(0.4, z / 1.2));
  const zoomFit = () => { setZoom(1); setPan({ x: 0, y: 0 }); };

  const wallPxW = wallW * scale;
  const wallPxH = wallH * scale;

  const renderedItems = useMemo(() => (
    [...items].sort((a, b) => (a.draw_order || 0) - (b.draw_order || 0))
  ), [items]);

  const visualLayerOf = useCallback((id) => {
    const order = [...items].sort((a, b) => (a.draw_order || 0) - (b.draw_order || 0));
    const idx = order.findIndex((i) => i.id === id);
    return idx < 0 ? null : idx + 1;
  }, [items]);

  useEffect(() => {
    if (!selectedId || !itemsListRef.current) return;
    const row = itemsListRef.current.querySelector(`[data-row-id="${selectedId}"]`);
    if (!row || typeof row.scrollIntoView !== "function") return;
    // Only scroll INSIDE the panel if the row is off-screen relative
    // to the panel itself. Never let this bubble into a page scroll.
    const panel = itemsListRef.current;
    const pRect = panel.getBoundingClientRect();
    const rRect = row.getBoundingClientRect();
    if (rRect.top < pRect.top || rRect.bottom > pRect.bottom) {
      row.scrollIntoView({ block: "nearest", inline: "nearest" });
    }
  }, [selectedId]);

  return (
    <div className="grid grid-cols-1 xl:grid-cols-[220px_1fr_220px] gap-px bg-border border border-border overflow-hidden"
         data-testid="designscene-root">
      {/* Palette */}
      <div className="bg-card p-3 h-[720px] overflow-y-auto min-w-0"
           style={{ scrollbarGutter: "stable" }}
           data-testid="designscene-palette">
        <div className="flex items-center justify-between mb-3">
          <div className="overline text-[10px]" data-testid="palette-title">Element</div>
          <button type="button" data-testid="btn-presets"
                  onClick={() => setShowPresets((v) => !v)}
                  className="text-[10px] uppercase tracking-widest text-muted-foreground hover:text-foreground inline-flex items-center gap-1">
            <Sparkles className="w-3 h-3" strokeWidth={1.5}/> Presets
          </button>
        </div>
        {showPresets && (
          <div className="mb-4 p-3 border border-border bg-muted space-y-2" data-testid="preset-menu">
            {PRESETS.map((p) => (
              <button key={p.key} data-testid={`preset-${p.key}`} onClick={() => insertPreset(p.key)}
                      className="w-full text-xs text-left px-2 py-2 border border-border hover:border-foreground bg-background">
                {p.label}
              </button>
            ))}
          </div>
        )}
        {/* Search-first palette. When search is empty the categories
            below honour the user's collapsed/expanded state. When
            the search input has text every match is auto-exposed
            with category context regardless of collapsed state. */}
        <div className="relative mb-3">
          <input type="text" value={paletteSearch}
                 onChange={(e) => setPaletteSearch(e.target.value)}
                 data-testid="palette-search"
                 aria-label="Filter elements"
                 placeholder="Filter elements…"
                 className="w-full pr-7 py-2 px-2 text-xs border border-border rounded-none bg-background outline-none focus:border-foreground min-h-[36px]"/>
          {paletteSearch && (
            <button type="button" data-testid="palette-search-clear"
                    onClick={() => setPaletteSearch("")}
                    className="absolute right-1 top-1/2 -translate-y-1/2 w-6 h-6 flex items-center justify-center text-muted-foreground hover:text-foreground">
              <X className="w-3.5 h-3.5" strokeWidth={1.5}/>
            </button>
          )}
        </div>
        {CATEGORIES.map((cat) => {
          const catItems = grouped[cat] || [];
          const q = paletteSearch.trim().toLowerCase();
          const matches = q
            ? catItems.filter((d) => d.label.toLowerCase().includes(q) || d.type.toLowerCase().includes(q))
            : catItems;
          if (q && matches.length === 0) return null;   // hide empty categories during search
          const isOpen = q ? true : !!expandedCats[cat]; // force-open during search
          return (
            <PaletteCategory key={cat} cat={cat}
                             items={matches}
                             totalCount={catItems.length}
                             isOpen={isOpen}
                             onToggle={() => toggleCategory(cat)}
                             onAdd={addItem}/>
          );
        })}
      </div>

      {/* Canvas */}
      <div className="bg-card p-2 relative h-[720px] min-w-0 overflow-hidden">
        <div className="flex items-center justify-between mb-3">
          <div className="flex items-center gap-3 text-[11px]">
            <button data-testid="btn-undo" onClick={undo}
                    disabled={!historyRef.current.past.length}
                    className="btn-outline text-[11px] disabled:opacity-40">
              <Undo2 className="w-3 h-3" strokeWidth={1.5}/> Undo
            </button>
            <button data-testid="btn-redo" onClick={redo}
                    disabled={!historyRef.current.future.length}
                    className="btn-outline text-[11px] disabled:opacity-40">
              <Redo2 className="w-3 h-3" strokeWidth={1.5}/> Redo
            </button>
            <span className="text-muted-foreground text-[10px]">Wall {wallW} × {wallH} mm</span>
          </div>
          <div className="flex items-center gap-2 text-[11px]">
            <SaveIndicator state={saveState}/>
            <EstimateIndicator status={estimateStatus}/>
            <button data-testid="btn-zoom-out" onClick={zoomOut} className="btn-outline p-1">
              <ZoomOut className="w-3 h-3" strokeWidth={1.5}/>
            </button>
            <span className="text-[10px] text-muted-foreground w-8 text-center">{Math.round(zoom * 100)}%</span>
            <button data-testid="btn-zoom-in" onClick={zoomIn} className="btn-outline p-1">
              <ZoomIn className="w-3 h-3" strokeWidth={1.5}/>
            </button>
            <button data-testid="btn-zoom-fit" onClick={zoomFit} className="btn-outline p-1" title="Fit">
              <Maximize className="w-3 h-3" strokeWidth={1.5}/>
            </button>
          </div>
        </div>

        <div ref={canvasRef} data-testid="schematic-canvas"
             className="relative bg-[color:var(--muted)] border border-border overflow-hidden touch-none"
             style={{ height: Math.max(400, wallPxH + 40) }}
             onMouseDownCapture={(e) => {
               // Capture phase runs BEFORE child handlers so that
               // stopPropagation() called by SceneItem's own
               // onMouseDown does not prevent this tracking. Records
               // whether the pointer originated inside an item /
               // selection overlay / resize handle so the following
               // click handler can skip clearing selection when the
               // browser dispatches the click on the shared ancestor
               // (canvas) after mousedown/mouseup hit different
               // children.
               const t = e.target;
               mousedownInsideItemRef.current = !!(t.closest && t.closest(
                 "[data-testid^='schematic-item-'], " +
                 "[data-testid^='selection-overlay-'], " +
                 "[data-testid^='resize-'], " +
                 "[data-testid^='select-layer-picker']"
               ));
             }}
             onTouchStartCapture={(e) => {
               const t = e.target;
               mousedownInsideItemRef.current = !!(t.closest && t.closest(
                 "[data-testid^='schematic-item-'], " +
                 "[data-testid^='selection-overlay-'], " +
                 "[data-testid^='resize-'], " +
                 "[data-testid^='select-layer-picker']"
               ));
             }}
             onClick={(e) => {
               if (mousedownInsideItemRef.current) {
                 mousedownInsideItemRef.current = false;
                 return;
               }
               const t = e.target;
               if (t.closest && t.closest(
                 "[data-testid^='schematic-item-'], " +
                 "[data-testid^='selection-overlay-'], " +
                 "[data-testid^='resize-'], " +
                 "[data-testid^='select-layer-picker']"
               )) return;
               setSelectedId(null);
               setMultiHitPicker(null);
             }}
             onContextMenu={openLayerPicker}>
          <div className="absolute border-2 border-foreground"
               data-testid="scene-wall"
               style={{
                 left: 20 + pan.x, top: 20 + pan.y,
                 width: wallPxW, height: wallPxH,
                 background: "#FAF9F5",
               }}/>
          {renderedItems.map((it) => (
            <SceneItem key={it.id} it={it} selectedId={selectedId}
                       pan={pan} scale={scale} wallH={wallH}
                       onStartMove={startMove} onStartResize={startResize}
                       onClick={handleItemClick}/>
          ))}

          {/* ── Always-visible Clearance overlays ─────────────────────
              Renders the canonical dashed Keep-Out envelope for every
              item with `clearance_config.enabled=true`, INDEPENDENT of
              selection state / move drag / resize drag / undo history
              — the visible envelope is derived from the same canonical
              geometry that drives Blocking and snap.  Subtle low-weight
              styling so the schematic stays readable.  pointer-events:
              none — never intercepts resize handles, move drag or
              clicks.  HIDDEN in Client View. */}
          {!clientView && renderedItems.map((it) => {
            const env = getClearanceEnvelope(it);
            if (!env) return null;
            const leftPx = 20 + pan.x + env.left * scale;
            const topPx  = 20 + pan.y + (wallH - env.top) * scale;
            const wPx    = (env.right - env.left) * scale;
            const hPx    = (env.top - env.bottom) * scale;
            const level  = env.enforcement || "warn";
            const border = level === "block"
              ? "1.5px dashed rgba(180, 60, 60, 0.55)"
              : level === "advisory"
                ? "1.25px dashed rgba(120, 120, 120, 0.5)"
                : "1.5px dashed rgba(166,138,100,0.55)";
            return (
              <div key={`cle-${it.id}`}
                   className="absolute"
                   data-testid={`clearance-persistent-${it.id}`}
                   data-clearance-owner={it.id}
                   data-clearance-mode={level}
                   style={{
                     left: leftPx, top: topPx, width: wPx, height: hPx,
                     border,
                     pointerEvents: "none",
                     // zIndex above scene items (typically 10-20) and
                     // below the SelectionInteractionOverlay (40) so
                     // resize handles remain clickable and the
                     // envelope is never obscured by scene items.
                     zIndex: 30,
                   }}/>
            );
          })}

          {/* ── Selection Interaction Overlay ────────────────────────
              UI-only layer rendered LAST (topmost) so an explicitly
              selected item remains draggable/resizable even when
              covered by a later-drawn or locked component. Never
              mutates draw_order, Host, Assembly, or Z. */}
          {selected && !readOnly && (
            <SelectionInteractionOverlay
              it={selected}
              pan={pan} scale={scale} wallH={wallH}
              onStartMove={startMove}
              onStartResize={startResize}
              onClick={handleItemClick}
            />
          )}

          {selected && (
            <DimOverlay it={selected} pan={pan} scale={scale} wallH={wallH}/>
          )}

          {/* ── Live Blocking / Warn / Advisory feedback ────────────
              During a drag, prominently highlight the envelopes of the
              other items whose Clearance is currently being pushed
              against. The envelope of every OTHER item stays hidden at
              rest (unchanged behaviour) — this overlay ONLY appears
              while the pointer is engaged with a Blocking / Warn /
              Advisory conflict. `pointer-events: none` so hit-testing
              on the underlying items is unchanged. */}
          {drag && (drag.kind === "move" || drag.kind === "resize") && (
            <ConflictOverlay
              items={items}
              conflicts={dragConflicts}
              pan={pan}
              scale={scale}
              wallH={wallH}
            />
          )}

          <div className="absolute text-[10px] text-muted-foreground"
               style={{ left: 20 + pan.x, top: 20 + pan.y + wallPxH + 4 }}>
            {wallW} mm
          </div>
          <div className="absolute text-[10px] text-muted-foreground"
               style={{ left: 4, top: 20 + pan.y + wallPxH / 2, transform: "rotate(-90deg)", transformOrigin: "0 0" }}>
            {wallH} mm
          </div>

          {multiHitPicker && (
            <SelectLayerPicker
              picker={multiHitPicker}
              selectedId={selectedId}
              onPick={(id) => { setSelectedId(id); setMultiHitPicker(null); setRightTab("properties"); }}
              onClose={() => setMultiHitPicker(null)}
              visualLayerOf={visualLayerOf}
            />
          )}
        </div>
        <div className="text-[10px] text-muted-foreground mt-2">
          Drag to move · Snaps to {SNAP_MM} mm (hold Alt to override) · ⌘Z/⌘⇧Z undo/redo · ⌘D duplicate · Del to remove
        </div>
      </div>

      {/* Right column: Properties | Schematic Items */}
      <div className="bg-card h-[720px] min-w-0 flex flex-col overflow-hidden"
           data-testid="designscene-properties">
        {selected && (
          <SelectedHeader
            item={selected}
            onLock={() => updateSelected({ locked: !selected.locked })}
            onDuplicate={() => duplicateItem(selected)}
            onDelete={() => requestDelete(selected)}
          />
        )}
        <div className="border-b border-border flex text-[11px]" data-testid="right-tabs">
          <TabBtn testid="tab-properties" label="Properties"
                  active={rightTab === "properties"}
                  onClick={() => setRightTab("properties")}/>
          <TabBtn testid="tab-schematic-items" label={`Schematic Items (${items.length})`}
                  active={rightTab === "items"}
                  onClick={() => setRightTab("items")}/>
        </div>
        <div className="flex-1 min-h-0 overflow-y-auto p-3"
             style={{ scrollbarGutter: "stable" }}
             ref={rightTab === "items" ? itemsListRef : undefined}
             data-testid={`tab-body-${rightTab}`}>
          {rightTab === "properties" && !selected && (
            <div className="text-xs text-muted-foreground">
              Select an element to edit its properties, or add one from the left palette.
            </div>
          )}
          {rightTab === "properties" && selected && (
            <PropertiesBody
              selected={selected}
              items={items}
              wallW={wallW}
              wallH={wallH}
              updateSelected={updateSelected}
              onCenterH={centerH} onCenterV={centerV} onCenterBoth={centerBoth}
              onAlignLeft={alignLeft} onAlignRight={alignRight}
              onAlignTop={alignTop}  onAlignBottom={alignBottom}
              onBringForward={bringForward} onSendBackward={sendBackward}
              onBringToFront={bringToFront} onSendToBack={sendToBack}
              onAssignMaterial={assignMaterial}
              onDoSync={doSyncItem}
              onSyncItem={onSyncItem}
              projectId={projectId}
              visualLayer={visualLayerOf(selected.id)}
            />
          )}
          {rightTab === "items" && (
            <SchematicItemsBody
              items={items}
              selectedId={selectedId}
              view={itemsView}
              onViewChange={setItemsView}
              search={itemsSearch}
              onSearchChange={setItemsSearch}
              onSelect={(id) => setSelectedId(id)}
              onRequestDelete={(it) => requestDelete(it)}
              visualLayerOf={visualLayerOf}
            />
          )}
        </div>
      </div>

      {pendingDelete && (
        <DeleteConfirm
          pending={pendingDelete}
          onCancel={() => setPendingDelete(null)}
          onDetach={() => performDelete("detach")}
          onCascade={() => performDelete("cascade")}
        />
      )}
    </div>
  );
}


// ── Small local components ─────────────────────────────────────────
function TabBtn({ testid, label, active, onClick }) {
  return (
    <button type="button" data-testid={testid}
            aria-selected={active}
            onClick={onClick}
            className={`flex-1 px-3 py-2 uppercase tracking-widest transition-colors ${active ? "bg-card text-foreground border-b-2 border-foreground -mb-px" : "text-muted-foreground hover:text-foreground"}`}>
      {label}
    </button>
  );
}

function PaletteCategory({ cat, items, totalCount, isOpen, onToggle, onAdd }) {
  const count = typeof totalCount === "number" ? totalCount : items.length;
  return (
    <div className="mb-1 border-b border-border/50 last:border-b-0"
         data-testid={`palette-category-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}>
      <button type="button"
              aria-expanded={isOpen}
              onClick={onToggle}
              title={cat}
              data-testid={`palette-cat-header-${cat.toLowerCase().replace(/[^a-z0-9]+/g, "-")}`}
              className="w-full flex items-center gap-2 justify-between px-1 py-2.5 min-h-[44px] text-left hover:text-foreground text-muted-foreground transition-colors">
        <span className="inline-flex items-center gap-1.5 min-w-0 flex-1">
          {isOpen
            ? <ChevronDown className="w-3 h-3 shrink-0" strokeWidth={1.75}/>
            : <ChevronRight className="w-3 h-3 shrink-0" strokeWidth={1.75}/>}
          <span className="overline text-[10px] truncate min-w-0">{cat}</span>
        </span>
        <span className="text-[10px] tabular-nums text-muted-foreground/70 shrink-0">{count}</span>
      </button>
      {isOpen && (
        <div className="space-y-1 pb-3">
          {items.map((def) => {
            const Icon = def.icon;
            return (
              <button key={def.type} data-testid={`add-schematic-${def.type}`}
                      onClick={() => onAdd(def.type)}
                      title={def.label}
                      className="w-full flex items-center gap-2 px-2 py-2 border border-transparent hover:border-foreground transition-colors text-left min-h-[44px]">
                <Icon className="w-4 h-4 shrink-0" strokeWidth={1.5}/>
                <span className="text-xs truncate min-w-0 flex-1">{def.label}</span>
              </button>
            );
          })}
        </div>
      )}
    </div>
  );
}

function SceneItem({ it, selectedId, pan, scale, wallH, onStartMove, onStartResize, onClick }) {
  const def = getComponent(it.type) || { color: "#5C5C5C", label: it.type };
  const isSel = it.id === selectedId;
  const isRecess = it.semantic_type === "recess" || def.semantic === "recess";
  const isSymbol = def.is_symbol;
  const svgY = 20 + pan.y + (wallH - (it.y + it.height)) * scale;

  // When this item is selected, the SelectionInteractionOverlay (zIndex 40)
  // sits above and handles drag/resize for buried-item scenarios. We do NOT
  // suppress pointer events on the SceneItem itself because doing so causes
  // the browser-generated CLICK event (fired AFTER mousedown → React state
  // commit → new render) to fall through to the canvas background and clear
  // the just-established selection. The overlay's higher z-index still wins
  // for buried-item interaction.
  const pointerBehaviour = {};

  if (isSymbol) {
    const sym = symbolFor(def.symbol_type);
    const symPx = def.symbol_px || 22;
    const cxPx = 20 + pan.x + (it.x + it.width / 2) * scale;
    const cyPx = 20 + pan.y + (wallH - (it.y + it.height / 2)) * scale;
    return (
      <div data-testid={`schematic-item-${it.id}`}
           onMouseDown={isSel ? undefined : (e) => onStartMove(e, it)}
           onTouchStart={isSel ? undefined : (e) => onStartMove(e, it)}
           onClick={isSel ? undefined : (e) => onClick(e, it)}
           className={`absolute select-none ${it.locked ? "cursor-not-allowed" : "cursor-move"}`}
           style={{
             left: cxPx - symPx / 2, top: cyPx - symPx / 2,
             width: symPx, height: symPx,
             outline: isSel ? "2px solid #A68A64" : "none",
             outlineOffset: 2,
             ...pointerBehaviour,
           }}
           title={def.label}>
        {sym ? sym.render(symPx) : <div className="w-full h-full bg-foreground/60"/>}
        {it.locked && (
          <Lock className="absolute -top-1 -right-1 w-3 h-3 text-foreground/70" strokeWidth={1.5}/>
        )}
      </div>
    );
  }

  const isCabinetry = def && def.category === "Cabinetry";
  const isCabinet = isCabinetry && (
    (it.cabinet_config && (it.cabinet_config.bays || []).length > 0)
    || !!it.left_end_profile || !!it.right_end_profile
  );

  return (
    <div data-testid={`schematic-item-${it.id}`}
         onMouseDown={isSel ? undefined : (e) => onStartMove(e, it)}
         onTouchStart={isSel ? undefined : (e) => onStartMove(e, it)}
         onClick={isSel ? undefined : (e) => onClick(e, it)}
         className={`absolute flex items-center justify-center text-white text-[10px] font-medium select-none ${it.locked ? "cursor-not-allowed" : "cursor-move"}`}
         style={{
           left: 20 + pan.x + it.x * scale,
           top: svgY,
           width: it.width * scale,
           height: it.height * scale,
           background: isRecess ? "transparent" : def.color,
           border: isSel ? "2px solid #A68A64"
                  : isRecess ? `2px dashed ${def.color}`
                  : "1px solid rgba(0,0,0,0.3)",
           opacity: isRecess ? 1 : 0.92,
           boxShadow: isSel ? "0 0 0 2px rgba(166,138,100,0.3)" : "none",
           ...pointerBehaviour,
         }}>
      {isCabinet ? (
        <div className="absolute inset-0 text-white/85" style={{ pointerEvents: "none" }}
             data-testid={`schematic-cabinet-bays-${it.id}`}>
          <CabinetBayPreview cabinetConfig={it.cabinet_config}
                             cabinetWidthMm={it.width}
                             cabinetHeightMm={it.height}
                             leftEndProfile={it.left_end_profile || null}
                             rightEndProfile={it.right_end_profile || null}
                             pxWidth={it.width * scale}
                             pxHeight={it.height * scale}
                             showBayNumbers={false}
                             testid={`schematic-cabinet-bays-svg-${it.id}`}/>
        </div>
      ) : null}
      <span className={`px-1 truncate ${isRecess ? "text-foreground" : ""}`}
            style={{ pointerEvents: "none" }}>
        {def.label}
      </span>
      {it.locked && (
        <Lock className="absolute top-1 right-1 w-3 h-3 text-white/70" strokeWidth={1.5}/>
      )}
    </div>
  );
}

function DimOverlay({ it, pan, scale, wallH }) {
  const svgY = 20 + pan.y + (wallH - (it.y + it.height)) * scale;
  return (
    <div className="absolute pointer-events-none text-[10px] bg-foreground text-background px-1"
         data-testid="dim-overlay"
         style={{ left: 20 + pan.x + it.x * scale, top: svgY - 18 }}>
      {Math.round(it.width)} × {Math.round(it.height)} mm · from floor: {Math.round(it.y)} mm
    </div>
  );
}

/**
 * ConflictOverlay — during a drag, paints the clearance envelopes of
 * OTHER items whose zone is currently being conflicted with. Rendered
 * with `pointer-events: none` so it never intercepts interaction. Not
 * shown at rest — only visible while a drag is engaged. Colour signals
 * enforcement level:
 *   Blocking  — red, solid stroke  (component is held at boundary)
 *   Warn      — amber, dashed
 *   Advisory  — muted blue, dotted
 */
function ConflictOverlay({ items, conflicts, pan, scale, wallH }) {
  const level = (id) => {
    if (conflicts.blocking.includes(id)) return "block";
    if (conflicts.warn.includes(id))     return "warn";
    if (conflicts.advisory.includes(id)) return "advisory";
    return null;
  };
  const style = {
    block:    { border: "2px solid rgba(220,60,60,0.9)",      bg: "rgba(220,60,60,0.06)"  },
    warn:     { border: "2px dashed rgba(200,140,40,0.9)",    bg: "rgba(200,140,40,0.06)" },
    advisory: { border: "1.5px dotted rgba(90,120,190,0.85)", bg: "rgba(90,120,190,0.04)" },
  };
  return (
    <>
      {items.map((it) => {
        const lvl = level(it.id);
        if (!lvl) return null;
        const env = getClearanceEnvelope(it);
        if (!env) return null;
        const s = style[lvl];
        // Convert canonical mm envelope to pixel-space (Y is flipped).
        const px = 20 + pan.x + env.left * scale;
        const py = 20 + pan.y + (wallH - env.top) * scale;
        const pw = (env.right - env.left) * scale;
        const ph = (env.top - env.bottom) * scale;
        return (
          <div key={it.id}
               data-testid={`clearance-conflict-${lvl}-${it.id}`}
               className="absolute pointer-events-none"
               style={{
                 left: px, top: py, width: pw, height: ph,
                 border: s.border, background: s.bg, zIndex: 42,
               }}/>
        );
      })}
    </>
  );
}

/**
 * Selection Interaction Overlay — UI-only pointer target that lives ABOVE
 * every rendered scene item. Ensures that an explicitly-selected item
 * remains draggable/resizable even when covered by a later-drawn or
 * locked component. Never mutates draw_order / Host / Assembly / Z.
 *
 * For a locked selected item, the overlay only paints the outline
 * (pointer-events: none on the move region) so the user can still
 * inspect but cannot manipulate — matching the existing Lock contract.
 */
function SelectionInteractionOverlay({ it, pan, scale, wallH, onStartMove, onStartResize, onClick }) {
  const def = getComponent(it.type) || {};
  const isSymbol = def.is_symbol;
  const locked = !!it.locked;
  const swallowClick = (e) => e.stopPropagation();

  if (isSymbol) {
    const symPx = def.symbol_px || 22;
    const cxPx = 20 + pan.x + (it.x + it.width / 2) * scale;
    const cyPx = 20 + pan.y + (wallH - (it.y + it.height / 2)) * scale;
    return (
      <div data-testid={`selection-overlay-${it.id}`}
           className="absolute"
           style={{
             left: cxPx - symPx / 2, top: cyPx - symPx / 2,
             width: symPx, height: symPx,
             zIndex: 40, cursor: locked ? "not-allowed" : "move",
             pointerEvents: locked ? "none" : "auto",
             outline: "2px solid #A68A64",
             outlineOffset: 2,
             background: "transparent",
           }}
           onMouseDown={(e) => onStartMove(e, it)}
           onTouchStart={(e) => onStartMove(e, it)}
           onClick={(e) => { e.stopPropagation(); onClick(e, it); }}
           title={locked ? "Locked" : "Drag to move"}/>
    );
  }

  const svgY = 20 + pan.y + (wallH - (it.y + it.height)) * scale;
  const leftPx = 20 + pan.x + it.x * scale;
  const wPx = it.width * scale;
  const hPx = it.height * scale;
  return (
    <div data-testid={`selection-overlay-${it.id}`}
         className="absolute select-none"
         style={{
           left: leftPx, top: svgY, width: wPx, height: hPx,
           zIndex: 40,
           cursor: locked ? "not-allowed" : "move",
           pointerEvents: locked ? "none" : "auto",
           outline: "2px solid #A68A64",
           outlineOffset: 0,
           boxShadow: "0 0 0 2px rgba(166,138,100,0.3)",
           background: "transparent",
         }}
         onMouseDown={(e) => onStartMove(e, it)}
         onTouchStart={(e) => onStartMove(e, it)}
         onClick={(e) => { e.stopPropagation(); onClick(e, it); }}
         title={locked ? "Locked" : "Drag to move · resize from any corner"}>
      {/* Clearance envelope — only rendered on the selected item so
          the canvas never gets permanently cluttered. Dashed line
          sits outside the outline. Pure visual — pointer-events off. */}
      {(() => {
        const cc = it.clearance_config;
        if (!cc || !cc.enabled) return null;
        const top    = Number(cc.top_mm)    || 0;
        const bottom = Number(cc.bottom_mm) || 0;
        const left   = Number(cc.left_mm)   || 0;
        const right  = Number(cc.right_mm)  || 0;
        if (top + bottom + left + right === 0) return null;
        return (
          <div className="absolute"
               data-testid={`clearance-envelope-${it.id}`}
               style={{
                 left: -left * scale,
                 top:  -top  * scale,
                 width:  wPx + (left + right) * scale,
                 height: hPx + (top + bottom) * scale,
                 border: "1.5px dashed rgba(166,138,100,0.7)",
                 pointerEvents: "none",
                 zIndex: 39,
               }}/>
        );
      })()}
      {!locked && (
        <>
          {/* Four corner resize handles. Each anchors the diagonally
              opposite physical corner. Visible handle ~14px; interactive
              hit target ~28-44px via absolute-positioned wrapper padding.
              Cursors: TL/BR = nwse-resize · TR/BL = nesw-resize. */}
          {[
            { k: "tl", cursor: "nwse-resize", style: { left: -14, top: -14 } },
            { k: "tr", cursor: "nesw-resize", style: { right: -14, top: -14 } },
            { k: "bl", cursor: "nesw-resize", style: { left: -14, bottom: -14 } },
            { k: "br", cursor: "nwse-resize", style: { right: -14, bottom: -14 } },
          ].map(({ k, cursor, style }) => (
            <div key={k}
                 data-testid={`resize-${k}-${it.id}`}
                 onMouseDown={(e) => onStartResize(e, it, k)}
                 onTouchStart={(e) => onStartResize(e, it, k)}
                 onClick={swallowClick}
                 className="absolute flex items-center justify-center"
                 style={{
                   ...style,
                   width: 28, height: 28,
                   zIndex: 41, pointerEvents: "auto",
                   cursor,
                 }}>
              <div className="w-3.5 h-3.5 bg-foreground border-2 border-white"
                   style={{ pointerEvents: "none" }}/>
            </div>
          ))}
          {/* Legacy alias for backwards compat with existing tests /
              screenshots that still reference `resize-<id>` — mirrors BR. */}
          <div data-testid={`resize-${it.id}`}
               style={{ position: "absolute", right: 0, bottom: 0, width: 1, height: 1, pointerEvents: "none", opacity: 0 }}/>
        </>
      )}
    </div>
  );
}


function _serialiseItem(it) {
  return {
    id: it.id, type: it.type,
    kind: it.kind || undefined,
    x: it.x, y: it.y, z: it.z ?? 0,
    width: it.width, height: it.height, depth: it.depth ?? 0,
    rotation: it.rotation ?? 0,
    material_id: it.material_id || null,
    product_id: it.product_id || null,
    material_slots: it.material_slots || undefined,
    semantic_type: it.semantic_type || undefined,
    quantity_mode: it.quantity_mode || undefined,
    wastage_pct: it.wastage_pct ?? 0,
    locked: !!it.locked,
    label: it.label || undefined,
    linked_line_item_id: it.linked_line_item_id || undefined,
    host_id:            it.host_id            || null,
    attachment:         it.attachment         || undefined,
    draw_order:         it.draw_order         ?? undefined,
    assembly_order:     it.assembly_order     ?? undefined,
    clearances:         it.clearances         || undefined,
    substrate:          it.substrate          || undefined,
    diagonal_inches:    it.diagonal_inches    ?? undefined,
    mounting_height_mm: it.mounting_height_mm ?? undefined,
    symbol_type:        it.symbol_type        || undefined,
    legend_code:        it.legend_code        || undefined,
    circuit:            it.circuit            || undefined,
    control_group:      it.control_group      || undefined,
    internal_notes:      it.internal_notes      || undefined,
    construction_notes:  it.construction_notes  || undefined,
    documentation_layers: it.documentation_layers || undefined,
    // Component configuration (Phase 1B / 2B-0 canonical fields).
    // These MUST survive round-trip through the autosave — otherwise
    // the 2D curved silhouette / bay preview / face preview / clearance
    // envelope silently regress to defaults on the next save.
    cabinet_config:     it.cabinet_config     || undefined,
    fireplace_config:   it.fireplace_config   || undefined,
    clearance_config:   it.clearance_config   || undefined,
    left_end_profile:   it.left_end_profile   || undefined,
    right_end_profile:  it.right_end_profile  || undefined,
    lighting:           it.lighting           || undefined,
  };
}
